/**
 * Modal para incluir, ordenar y nombrar los campos de cada fuente.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ink, studio } from '../studioUi';
import {
    joinKeyNamesForSource,
    normalizeSelectedColumns,
    selectedColumnAlias,
    selectedColumnOrder,
    selectedColumnRole,
    sourceLabel,
} from './engineConfig';

function defaultRole(col) {
    return (col?.inferred_type || 'string') === 'number' ? 'metric' : 'dimension';
}

function buildRows(source, columns, joins) {
    const keys = new Set(joinKeyNamesForSource(source?.id, joins).map((name) => String(name).toLowerCase()));
    const byName = new Map((columns || []).map((col) => [col.name, col]));
    const restricted = Boolean(source?.selectedColumns);
    const order = restricted
        ? selectedColumnOrder(source, columns)
        : (columns || []).map((col) => col.name);
    const included = order
        .map((name) => byName.get(name))
        .filter(Boolean)
        .map((col) => ({
            name: col.name,
            role: selectedColumnRole(source, col.name, col.inferred_type),
            alias: selectedColumnAlias(source, col.name),
            locked: keys.has(String(col.name).toLowerCase()),
            inferred_type: col.inferred_type || 'string',
        }));
    if (!restricted) {
        columns.forEach((col) => {
            if (keys.has(String(col.name).toLowerCase()) && !included.some((row) => row.name === col.name)) {
                included.push({
                    name: col.name,
                    role: defaultRole(col),
                    alias: col.name,
                    locked: true,
                    inferred_type: col.inferred_type || 'string',
                });
            }
        });
    }
    const used = new Set(included.map((row) => row.name));
    const available = (columns || []).filter((col) => !used.has(col.name));
    return { included, available, keys };
}

function toPayload(included, columns) {
    const allNames = (columns || []).map((col) => col.name);
    const aliases = {};
    included.forEach((row) => {
        const label = String(row.alias || '').trim();
        if (label && label !== row.name) aliases[row.name] = label;
    });
    const sameSet = allNames.length && allNames.length === included.length
        && allNames.every((name) => included.some((row) => row.name === name));
    const sameOrder = sameSet && allNames.every((name, index) => included[index]?.name === name);
    const sameRoles = included.every((row) => row.role === defaultRole({ inferred_type: row.inferred_type }));
    const noAliases = !Object.keys(aliases).length;
    if (sameSet && sameOrder && sameRoles && noAliases) return null;
    return normalizeSelectedColumns({
        dimensions: included.filter((row) => row.role === 'dimension').map((row) => row.name),
        metrics: included.filter((row) => row.role === 'metric').map((row) => row.name),
        order: included.map((row) => row.name),
        aliases,
    });
}

function moveItem(list, from, to) {
    if (from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
    const next = list.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
}

export default function SourceFieldsModal({
    open,
    onClose,
    dataSources = [],
    joins = [],
    schemasBySource = {},
    activeSourceId,
    onSelectSource,
    onChange,
}) {
    const [search, setSearch] = useState('');
    const [draft, setDraft] = useState([]);
    const [dragIndex, setDragIndex] = useState(null);
    const source = dataSources.find((item) => item.id === activeSourceId) || dataSources[0] || null;
    const columns = schemasBySource[source?.id]?.columns || [];

    const schemaKey = columns.map((col) => col.name).join('|');
    const joinKeys = joinKeyNamesForSource(source?.id, joins).join('|');

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open || !source) return;
        setDraft(buildRows(source, columns, joins).included);
        setSearch('');
    }, [open, source?.id, schemaKey, joinKeys]);

    const available = useMemo(() => {
        const used = new Set(draft.map((row) => row.name));
        const q = search.trim().toLowerCase();
        return columns.filter((col) => !used.has(col.name) && (!q || String(col.name).toLowerCase().includes(q)));
    }, [columns, draft, search]);

    if (!open) return null;

    const commit = (nextIncluded) => {
        setDraft(nextIncluded);
        if (source) onChange(source.id, toPayload(nextIncluded, columns));
    };

    const addField = (col) => {
        commit([...draft, {
            name: col.name,
            role: defaultRole(col),
            alias: col.name,
            locked: false,
            inferred_type: col.inferred_type || 'string',
        }]);
    };

    const addAll = () => {
        const used = new Set(draft.map((row) => row.name));
        const extra = columns.filter((col) => !used.has(col.name)).map((col) => ({
            name: col.name,
            role: defaultRole(col),
            alias: col.name,
            locked: false,
            inferred_type: col.inferred_type || 'string',
        }));
        commit([...draft, ...extra]);
    };

    const includeAllDefault = () => {
        commit(columns.map((col) => ({
            name: col.name,
            role: defaultRole(col),
            alias: col.name,
            locked: joinKeyNamesForSource(source?.id, joins).some((name) => name.toLowerCase() === String(col.name).toLowerCase()),
            inferred_type: col.inferred_type || 'string',
        })));
    };

    const patchRow = (index, partial) => {
        commit(draft.map((row, i) => (i === index ? { ...row, ...partial } : row)));
    };

    const removeRow = (index) => {
        if (draft[index]?.locked) return;
        commit(draft.filter((_, i) => i !== index));
    };

    const dimCount = draft.filter((row) => row.role === 'dimension').length;
    const metCount = draft.filter((row) => row.role === 'metric').length;

    return (
        <div style={ui.overlay} onMouseDown={onClose}>
            <div style={ui.sheet} onMouseDown={(event) => event.stopPropagation()}>
                <header style={ui.head}>
                    <div>
                        <p style={studio.kicker}>Campos incluidos</p>
                        <h2 style={ui.title}>{source ? sourceLabel(source) : 'Fuente'}</h2>
                        <p style={ui.sub}>
                            Incluye solo lo que el informe necesita, ordénalo y ponle un nombre claro. Las claves de unión no se pueden quitar.
                        </p>
                    </div>
                    <div style={ui.headActions}>
                        <button type="button" style={ui.ghost} onClick={includeAllDefault}>Incluir todas</button>
                        <button type="button" style={ui.close} onClick={onClose}>Listo</button>
                    </div>
                </header>
                <div style={ui.body}>
                    <aside style={ui.rail}>
                        <span style={ui.railLabel}>Fuentes</span>
                        {dataSources.map((item) => {
                            const active = item.id === source?.id;
                            const count = schemasBySource[item.id]?.columns?.length || 0;
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => onSelectSource(item.id)}
                                    style={{
                                        ...ui.railChip,
                                        backgroundColor: active ? ink.accentSoft : ink.surface,
                                        borderColor: active ? ink.accent : ink.lineStrong,
                                        color: active ? ink.accentText : ink.muted,
                                    }}
                                >
                                    <span style={ui.railKind}>{item.kind === 'join' ? 'Unión' : 'Hoja'}</span>
                                    <span style={ui.railName}>{sourceLabel(item)}</span>
                                    <span style={ui.railMeta}>{count ? `${count} campos` : 'Sin esquema'}</span>
                                </button>
                            );
                        })}
                    </aside>

                    <section style={ui.pane}>
                        <div style={ui.paneHead}>
                            <div>
                                <span style={studio.kicker}>Disponibles</span>
                                <p style={ui.paneHint}>Pulsa + para incluirlos a la derecha.</p>
                            </div>
                            <button type="button" style={ui.link} onClick={addAll} disabled={!available.length}>Añadir visibles</button>
                        </div>
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar en la hoja…"
                            style={ui.search}
                        />
                        <div style={ui.list}>
                            {!columns.length && (
                                <p style={ui.empty}>Conecta la fuente para ver sus columnas.</p>
                            )}
                            {columns.length > 0 && !available.length && (
                                <p style={ui.empty}>Todos los campos ya están incluidos.</p>
                            )}
                            {available.map((col) => (
                                <div key={col.name} style={ui.availRow}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={ui.fieldName}>{col.name}</div>
                                        <div style={ui.fieldMeta}>{col.inferred_type === 'number' ? 'Número' : (col.inferred_type === 'date' ? 'Fecha' : 'Texto')}</div>
                                    </div>
                                    <button type="button" style={ui.addBtn} onClick={() => addField(col)}>+ Incluir</button>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section style={ui.paneWide}>
                        <div style={ui.paneHead}>
                            <div>
                                <span style={studio.kicker}>En el informe</span>
                                <p style={ui.paneHint}>{draft.length} campos · {dimCount} dimensiones · {metCount} métricas. Arrastra el asa para ordenar.</p>
                            </div>
                            <button
                                type="button"
                                style={ui.link}
                                onClick={() => commit(draft.filter((row) => row.locked))}
                                disabled={!draft.some((row) => !row.locked)}
                            >
                                Quitar no clave
                            </button>
                        </div>
                        <div style={ui.tableHead}>
                            <span style={ui.thOrder}> </span>
                            <span style={ui.thName}>Campo</span>
                            <span style={ui.thAlias}>Nombre visible</span>
                            <span style={ui.thRole}>Uso</span>
                            <span style={ui.thAct}> </span>
                        </div>
                        <div style={ui.includedList}>
                            {!draft.length && (
                                <p style={ui.empty}>No hay campos incluidos. Añádelos desde la columna de la izquierda.</p>
                            )}
                            {draft.map((row, index) => (
                                <div
                                    key={row.name}
                                    style={{
                                        ...ui.includedRow,
                                        outline: dragIndex === index ? `2px solid ${ink.accent}` : 'none',
                                    }}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={() => {
                                        if (dragIndex == null) return;
                                        commit(moveItem(draft, dragIndex, index));
                                        setDragIndex(null);
                                    }}
                                >
                                    <button
                                        type="button"
                                        draggable
                                        title="Arrastrar para ordenar"
                                        onDragStart={() => setDragIndex(index)}
                                        onDragEnd={() => setDragIndex(null)}
                                        style={ui.handle}
                                    >
                                        ⋮⋮
                                    </button>
                                    <div style={ui.fieldBlock}>
                                        <div style={ui.fieldName}>{row.name}</div>
                                        <div style={ui.tags}>
                                            {row.locked ? <span style={ui.lock}>clave</span> : null}
                                            <span style={row.role === 'metric' ? ui.metTag : ui.dimTag}>
                                                {row.inferred_type === 'number' ? 'núm' : (row.inferred_type === 'date' ? 'fecha' : 'txt')}
                                            </span>
                                        </div>
                                    </div>
                                    <input
                                        style={ui.alias}
                                        value={row.alias}
                                        onChange={(e) => patchRow(index, { alias: e.target.value })}
                                        placeholder={row.name}
                                        title="Nombre que verás en la paleta y en el gráfico"
                                    />
                                    <select
                                        style={ui.role}
                                        value={row.role}
                                        onChange={(e) => patchRow(index, { role: e.target.value })}
                                    >
                                        <option value="dimension">Dimensión</option>
                                        <option value="metric">Métrica</option>
                                    </select>
                                    <div style={ui.rowActs}>
                                        <button type="button" style={ui.icon} disabled={index === 0} onClick={() => commit(moveItem(draft, index, index - 1))} title="Subir">↑</button>
                                        <button type="button" style={ui.icon} disabled={index === draft.length - 1} onClick={() => commit(moveItem(draft, index, index + 1))} title="Bajar">↓</button>
                                        <button type="button" style={ui.iconKill} disabled={row.locked} onClick={() => removeRow(index)} title={row.locked ? 'Clave de unión' : 'Quitar'}>×</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

const ui = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.5)',
        zIndex: 62,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        padding: 16,
    },
    sheet: {
        width: 'min(1280px, 100%)',
        height: 'min(92vh, 920px)',
        backgroundColor: ink.surface,
        borderRadius: 18,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 28px 80px rgba(28, 29, 33, 0.28)',
    },
    head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        padding: '16px 18px 12px',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
        flexShrink: 0,
    },
    title: { margin: '4px 0 0', fontSize: '1.2rem', color: ink.text },
    sub: { margin: '6px 0 0', fontSize: '0.78rem', color: ink.muted, maxWidth: 640, lineHeight: 1.4 },
    headActions: { display: 'flex', gap: 8, flexShrink: 0 },
    ghost: {
        appearance: 'none',
        padding: '8px 14px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.accentText,
        cursor: 'pointer',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    close: {
        appearance: 'none',
        padding: '8px 14px',
        borderRadius: 999,
        borderWidth: 0,
        backgroundColor: ink.accent,
        color: ink.surface,
        cursor: 'pointer',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    body: {
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 180px) minmax(220px, 280px) minmax(0, 1fr)',
        gap: 0,
    },
    rail: {
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        overflow: 'auto',
        borderRightWidth: 1,
        borderRightStyle: 'solid',
        borderRightColor: ink.line,
        backgroundColor: ink.wash,
    },
    railLabel: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: ink.faint,
        paddingLeft: 4,
    },
    railChip: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        cursor: 'pointer',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        backgroundColor: ink.surface,
    },
    railKind: { fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.75 },
    railName: { fontSize: '0.8rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' },
    railMeta: { fontSize: '0.68rem', color: ink.faint },
    pane: {
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minHeight: 0,
        borderRightWidth: 1,
        borderRightStyle: 'solid',
        borderRightColor: ink.line,
        backgroundColor: ink.surface,
    },
    paneWide: {
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minHeight: 0,
        minWidth: 0,
        backgroundColor: ink.surface,
    },
    paneHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
    paneHint: { margin: '4px 0 0', fontSize: '0.72rem', color: ink.muted, lineHeight: 1.35 },
    search: {
        width: '100%',
        boxSizing: 'border-box',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: 10,
        padding: '8px 10px',
        fontSize: '0.82rem',
        fontFamily: 'inherit',
        backgroundColor: ink.surface,
        color: ink.text,
    },
    list: { flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 },
    availRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 10,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        backgroundColor: ink.wash,
    },
    fieldName: { fontSize: '0.82rem', fontWeight: 700, color: ink.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    fieldMeta: { fontSize: '0.68rem', color: ink.faint, marginTop: 2 },
    addBtn: {
        appearance: 'none',
        flexShrink: 0,
        padding: '5px 10px',
        borderRadius: 999,
        borderWidth: 0,
        backgroundColor: ink.accentSoft,
        color: ink.accentText,
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: '0.72rem',
        fontFamily: 'inherit',
    },
    link: {
        appearance: 'none',
        borderWidth: 0,
        backgroundColor: 'transparent',
        color: ink.accentText,
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        fontFamily: 'inherit',
        padding: 0,
    },
    empty: { margin: 8, fontSize: '0.78rem', color: ink.faint, lineHeight: 1.4 },
    tableHead: {
        display: 'grid',
        gridTemplateColumns: '28px minmax(120px, 1.1fr) minmax(140px, 1.2fr) 120px 92px',
        gap: 8,
        padding: '0 8px',
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: ink.faint,
    },
    thOrder: {},
    thName: {},
    thAlias: {},
    thRole: {},
    thAct: {},
    includedList: { flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 },
    includedRow: {
        display: 'grid',
        gridTemplateColumns: '28px minmax(120px, 1.1fr) minmax(140px, 1.2fr) 120px 92px',
        gap: 8,
        alignItems: 'center',
        padding: '8px',
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        backgroundColor: ink.wash,
    },
    handle: {
        appearance: 'none',
        borderWidth: 0,
        backgroundColor: 'transparent',
        color: ink.faint,
        cursor: 'grab',
        fontSize: '0.85rem',
        letterSpacing: '-0.12em',
        fontFamily: 'inherit',
        padding: 0,
    },
    fieldBlock: { minWidth: 0 },
    tags: { display: 'flex', gap: 4, marginTop: 3 },
    lock: {
        fontSize: '0.58rem',
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: ink.accentText,
        backgroundColor: ink.accentSoft,
        borderRadius: 999,
        padding: '1px 6px',
    },
    dimTag: {
        fontSize: '0.58rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        color: '#174ea6',
        backgroundColor: '#e8f0fe',
        borderRadius: 999,
        padding: '1px 6px',
    },
    metTag: {
        fontSize: '0.58rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        color: '#137333',
        backgroundColor: '#e6f4ea',
        borderRadius: 999,
        padding: '1px 6px',
    },
    alias: {
        width: '100%',
        boxSizing: 'border-box',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: 8,
        padding: '7px 8px',
        fontSize: '0.8rem',
        fontFamily: 'inherit',
        backgroundColor: ink.surface,
        color: ink.text,
    },
    role: {
        width: '100%',
        boxSizing: 'border-box',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        borderRadius: 8,
        padding: '7px 6px',
        fontSize: '0.78rem',
        fontFamily: 'inherit',
        backgroundColor: ink.surface,
        color: ink.text,
    },
    rowActs: { display: 'flex', gap: 4, justifyContent: 'flex-end' },
    icon: {
        appearance: 'none',
        width: 26,
        height: 26,
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    iconKill: {
        appearance: 'none',
        width: 26,
        height: 26,
        borderRadius: 8,
        borderWidth: 0,
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontWeight: 700,
    },
};
